import 'date-input-polyfill-react'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import React, { useEffect, useState } from 'react'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'

import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { noteSchema } from './NoteSchema'

import styles from '../Dialog.module.sass'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import HFDatepicker from 'components/HFInputs/HFDatepicker'
import HFCheckbox from 'components/HFInputs/HFCheckbox'
import HFTextInput from 'components/HFInputs/HFTextInput'
import moment from 'moment'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const INITIAL_DATE = moment().format('YYYY-MM-DD')

const NoteDialog = (props) => {
	const { className, data, dismiss, resolve } = props
	const { note, processId } = data
	const { i18n, pushNotification, expireSession } = useGlobal()
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(noteSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, watch, reset, setValue } = methods

	const [reminder] = watch(['reminder'])

	const onSubmit = (formData) => {
		const url =
			!note && processId
				? `${API_URL}/api/notes/${processId}`
				: `${API_URL}/api/notes${note ? `/${note?.id}` : ''}`

		setError(null)
		setSubmitting(true)

		fetch(
			url,
			fetchOptions({
				method: note ? 'PUT' : 'POST',
				body: formData,
			})
		)
			.then(async (response) => {
				if (response.ok) {
					if (note) {
						pushNotification(
							undefined,
							'success',
							i18n`Note updated successfully!`
						)
					} else {
						pushNotification(
							undefined,
							'success',
							i18n`Note inserted successfully!`
						)
					}
					resolve()
				} else {
					setError([(await parseJSONContent(response)).message])
				}
			})
			.catch((err) => {
				if (err.status === '401' || err.status === 401) {
					expireSession()
				}
				const errorMessage = translateErrorMessage(err, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		if (!formState.isDirty && formState.submitCount === 0) {
			reset &&
				reset(
					note
						? {
								...note,
								reminderDate: note?.reminderDate
									? note.reminderDate?.UTC
									: INITIAL_DATE,
								reminder: note?.reminder || false,
								emphasize: note?.emphasize || false,
						  }
						: {
								reminderDate: INITIAL_DATE,
								emphasize: !processId ? true : false,
						  }
				)
		}
	}, [reset, formState.isDirty, formState.submitCount, note, processId])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	useEffect(() => {
		if (!processId && !reminder) {
			setValue('emphasize', true)
		}
	}, [processId, reminder, setValue])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_note'}
					className={`${styles['root'] || ''} ${className || ''}`}
					onSubmit={handleSubmit(onSubmit)}
					disabled={submitting}
				>
					<fieldset>
						<h5 className={styles['dialog-title']}>
							{i18n`Enter you message`}
						</h5>
						<HFTextInput
							name='note'
							placeholder={i18n`Note`}
							multiline={true}
							rows={5}
							noResize
							required
						/>
						<HFCheckbox
							className={`${styles['checkbox']} ${
								!processId && !reminder
									? styles['--disabled']
									: ''
							}`}
							name='emphasize'
							label={i18n`Emphasize`}
						/>
						<HFCheckbox
							className={styles['checkbox']}
							name='reminder'
							label={i18n`Reminder`}
						/>
						{reminder && (
							<HFDatepicker
								name='reminderDate'
								type='date'
								initialDate={
									note
										? note?.reminderDate
											? note.reminderDate?.momentDate
											: INITIAL_DATE
										: INITIAL_DATE
								}
							/>
						)}
					</fieldset>
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={'form_note'} primary disabled={submitting}>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default NoteDialog
